/**
 * usageGlass — the shared pieces of the Usage report's glass pages: the stat
 * tiles, the developers-vs-Agents split, the Claude comparison bars, and the
 * note on how the Claude side is priced. Glass material with the attention
 * accent; savings figures in the shipped green (`--accent-active`).
 */
import type { CSSProperties, ReactNode } from "react";

import type { YagniCodeFrontierBasis, YagniCodeFrontierComparison } from "@yagni/shared";
import { basisSavingsPercent, formatUsd } from "./usageReport.js";

/** Machine fact: money and counts in the mono voice. */
const MONO = "font-[family-name:var(--font-mono)]";

export const STAT_LABEL: CSSProperties = {
  fontFamily: "var(--font-fact)",
  fontSize: 9.5,
  fontWeight: 600,
  letterSpacing: "0.09em",
  textTransform: "uppercase",
  color: "var(--text-tertiary)",
};


export const SAVE_COLOR = "var(--accent-active)";

/** The drills' stat tiles, on the grammar's `.tiles` / `.tile`. */
export function GlassTiles({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <div className="tiles" data-testid={testId}>
      {children}
    </div>
  );
}

export function GlassStat({
  label,
  value,
  sub,
  tone,
  title,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  tone?: "save";
  title?: string;
}) {
  return (
    <div className="tile" title={title}>
      <span className={tone === "save" ? "n green" : "n"}>{value}</span>
      <span className="l">{label}</span>
      {sub !== undefined && (
        <span className="s" style={tone === "save" ? { color: SAVE_COLOR } : undefined}>
          {sub}
        </span>
      )}
    </div>
  );
}

export function GlassCard({
  children,
  testId,
  style,
}: {
  children: ReactNode;
  testId?: string;
  style?: CSSProperties;
}) {
  return (
    <div className="tile" data-testid={testId} style={{ padding: "14px 16px", display: "block", ...style }}>
      {children}
    </div>
  );
}

/** "$1,234.56 · 41%": a saved figure with its share, or the figure alone.
 * Clamped at zero: a negative saving is the no-savings state, never "$-12". */
export function savedWithShare(
  savedMillicents: number,
  percent: number | null,
  format: (millicents: number) => string = formatUsd,
): string {
  const saved = format(Math.max(0, savedMillicents));
  return percent === null ? saved : `${saved} · ${percent}%`;
}

/** Developers beside Agents, as one bar of the bill. Hidden when only one
 * side has spend: a bar that is all one colour says nothing the tile does
 * not. */
export function SplitBar({
  developersMillicents,
  agentsMillicents,
}: {
  developersMillicents: number;
  agentsMillicents: number;
}) {
  const total = developersMillicents + agentsMillicents;
  if (developersMillicents <= 0 || agentsMillicents <= 0) return null;
  const developerShare = Math.round((developersMillicents / total) * 100);
  return (
    <div data-testid="usage-split" style={{ marginTop: 14 }}>
      <div
        role="img"
        aria-label={`Developers ${formatUsd(developersMillicents)} (${developerShare}%), Agents ${formatUsd(agentsMillicents)} (${100 - developerShare}%)`}
        style={{
          display: "flex",
          height: 8,
          borderRadius: 999,
          overflow: "hidden",
          background: "rgba(var(--ink-rgb), 0.08)",
        }}
      >
        <div style={{ width: `${developerShare}%`, background: "var(--accent)" }} />
        <div style={{ flex: 1, background: "rgba(var(--ink-rgb), 0.3)" }} />
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 6,
          fontFamily: "var(--font-fact)",
          fontSize: 10.5,
          color: "var(--text-tertiary)",
        }}
      >
        <span>
          <Swatch color="var(--accent)" /> Developers {formatUsd(developersMillicents)} · {developerShare}%
        </span>
        <span>
          <Swatch color="rgba(var(--ink-rgb), 0.3)" /> Agents {formatUsd(agentsMillicents)} ·{" "}
          {100 - developerShare}%
        </span>
      </div>
    </div>
  );
}

function Swatch({ color }: { color: string }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 8,
        height: 8,
        borderRadius: 2,
        background: color,
        marginRight: 4,
        verticalAlign: "baseline",
      }}
    />
  );
}

const CLAUDE_BASE = "rgba(var(--ink-rgb), 0.32)";
const CLAUDE_WRITING = "rgba(var(--ink-rgb), 0.14)";

export interface ComparisonBar {
  key: string;
  label: string;
  /** What YAGNI charged, or the Claude basis to draw. */
  value: { kind: "yagni"; millicents: number } | { kind: "claude"; basis: YagniCodeFrontierBasis };
  selected?: boolean;
}

/**
 * The drill's four bars on one scale: what YAGNI charged, then Claude on the
 * matched tier, all Sonnet, and all Opus, each with its prompt-writing share
 * drawn as its own segment so the reader sees how much of the Claude figure
 * is the cache-write premium.
 */
export function ComparisonBars({ bars }: { bars: ComparisonBar[] }) {
  const max = Math.max(
    1,
    ...bars.map((bar) => (bar.value.kind === "yagni" ? bar.value.millicents : bar.value.basis.totalMillicents)),
  );
  const pct = (millicents: number) => `${(Math.max(0, millicents) / max) * 100}%`;
  return (
    <div data-testid="comparison-bars">
      {bars.map((bar) => {
        const total = bar.value.kind === "yagni" ? bar.value.millicents : bar.value.basis.totalMillicents;
        const writing = bar.value.kind === "claude" ? bar.value.basis.promptWritingMillicents : 0;
        const label =
          bar.value.kind === "claude" && writing > 0
            ? `${bar.label}: ${formatUsd(total)}, of which ${formatUsd(writing)} prompt writing`
            : `${bar.label}: ${formatUsd(total)}`;
        return (
          <div
            key={bar.key}
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(110px, 160px) 1fr minmax(72px, auto)",
              alignItems: "center",
              gap: 12,
              padding: "6px 0",
            }}
          >
            <span
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 12.5,
                fontWeight: bar.selected ? 600 : 400,
                color: bar.selected ? "var(--text-primary)" : "var(--text-secondary)",
              }}
            >
              {bar.label}
            </span>
            <div
              role="img"
              aria-label={label}
              style={{
                display: "flex",
                height: 14,
                borderRadius: 4,
                overflow: "hidden",
                background: "rgba(var(--ink-rgb), 0.05)",
              }}
            >
              {bar.value.kind === "yagni" ? (
                <div style={{ width: pct(total), background: "var(--accent)" }} />
              ) : (
                <>
                  <div style={{ width: pct(total - writing), background: CLAUDE_BASE }} />
                  <div style={{ width: pct(writing), background: CLAUDE_WRITING }} />
                </>
              )}
            </div>
            <span
              style={{
                fontFamily: "var(--font-fact)",
                fontSize: 12.5,
                textAlign: "right",
                fontVariantNumeric: "tabular-nums",
                color: "var(--text-primary)",
              }}
            >
              {formatUsd(total)}
            </span>
          </div>
        );
      })}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 14,
          marginTop: 8,
          fontFamily: "var(--font-fact)",
          fontSize: 10.5,
          color: "var(--text-tertiary)",
        }}
      >
        <span>
          <Swatch color="var(--accent)" /> YAGNI charged
        </span>
        <span>
          <Swatch color={CLAUDE_BASE} /> Claude, same tokens
        </span>
        <span>
          <Swatch color={CLAUDE_WRITING} /> Claude prompt writing
        </span>
      </div>
    </div>
  );
}

/** The two table cells every Agent table ends on: the same tokens at Claude
 * list (matched tier), and what was saved with its share. Dashes when there
 * is no comparison to show (a rollup row, a failed pricing read). */
export function ClaudeCells({
  frontier,
  pad = "py-3",
}: {
  frontier: YagniCodeFrontierComparison | undefined;
  pad?: string;
}) {
  if (!frontier) {
    return (
      <>
        <td className={`px-4 ${pad} text-right text-[var(--text-tertiary)]`}>-</td>
        <td className={`px-4 ${pad} text-right text-[var(--text-tertiary)]`}>-</td>
      </>
    );
  }
  const percent = basisSavingsPercent(frontier.matched);
  return (
    <>
      <td className={`px-4 ${pad} text-right ${MONO} text-[var(--text-secondary)]`}>
        {formatUsd(frontier.matched.totalMillicents)}
      </td>
      <td className={`px-4 ${pad} text-right ${MONO}`} style={{ color: SAVE_COLOR }}>
        {formatUsd(frontier.matched.savingsMillicents)}
        {percent !== null ? ` · ${percent}%` : ""}
      </td>
    </>
  );
}

/** How the Claude side is priced, in the reader's words. Shown on every page
 * that prints a Claude figure, so the number never travels without its
 * method. */
export function ClaudeMethodNote() {
  return (
    <GlassCard testId="claude-method" style={{ marginTop: 28 }}>
      <h3 className="m-0" style={{ ...STAT_LABEL, fontSize: 10.5 }}>
        How we price the Claude side
      </h3>
      <ul
        style={{
          margin: "10px 0 0",
          paddingLeft: 18,
          fontFamily: "var(--font-ui)",
          fontSize: 13,
          lineHeight: 1.6,
          color: "var(--text-secondary)",
        }}
      >
        <li>
          The same token counts, at Anthropic's published list price in force on the day of each
          dispatch. From October 6, 2026 that is Claude Sonnet 5.5, Opus 5.5, Haiku 4.5 and Fable
          5.1.
        </li>
        <li>
          Tokens read from cache are priced at Claude's cached input rate. From October 6, 2026,
          new prompt tokens count as 5 minute cache writes (1.25x the input price), because Claude
          Code writes its prompt to the cache on every turn. That premium is the Claude prompt
          writing figure.
        </li>
        <li>
          The 1 hour cache rate (2x) is not used, so the Claude figure stays conservative.
        </li>
        <li>
          It is a price comparison on the same tokens, not a replay: Claude might have used a
          different number of tokens for the same work.
        </li>
      </ul>
    </GlassCard>
  );
}
